/**
 * Form to create or edit a meaning_grammeme for the current meaning.
 * Dropdown for grammatical_feature (from language_grammatical_feature_priority for the meaning's language, excluding irrelevant),
 * then dropdown for grammeme (filtered by selected feature).
 */
import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { gql } from '@apollo/client'
import { apolloClient } from '../utils/apollo.js'
import { t } from '../utils/i18n.js'
import '@material/web/button/filled-button.js'
import '@material/web/select/filled-select.js'
import '@material/web/select/select-option.js'
import styles from './meaning-grammeme-form.css?inline'

const LANGUAGE_GRAMMATICAL_FEATURE_PRIORITIES_QUERY = gql`
  query LanguageGrammaticalFeaturePriorities {
    languageGrammaticalFeaturePriorities {
      id
      language { id }
      feature { id name }
      priority { id name }
    }
  }
`

const GRAMMEMES_QUERY = gql`
  query Grammemes {
    grammemes {
      id
      value
      feature { id name }
    }
  }
`

const CREATE_MEANING_GRAMMEME_MUTATION = gql`
  mutation CreateMeaningGrammeme($input: CreateMeaningGrammemeInput!) {
    createMeaningGrammeme(input: $input) {
      id
      meaning { id }
      grammeme { id value feature { id name } }
    }
  }
`

interface LGFP {
  id: string
  language: { id: string }
  feature: { id: string; name: string }
  priority: { id: string; name: string }
}

interface GrammemeOption {
  id: string
  value: string
  feature: { id: string; name: string }
}

@customElement('meaning-grammeme-form')
export class MeaningGrammemeForm extends LitElement {
  /** Current meaning ID (required to create a meaning_grammeme) */
  @property({ type: String }) meaningId = ''

  /** Language ID of the meaning (used to filter grammatical features for this language) */
  @property({ type: String }) languageId = ''

  /** Grammeme IDs already assigned to this meaning (e.g. to disable or hide in dropdown) */
  @property({ type: Array }) existingGrammemeIds: string[] = []

  /** Grammatical feature IDs already assigned to this meaning (hide these from the feature dropdown) */
  @property({ type: Array }) existingFeatureIds: string[] = []

  @state() private lgfpList: LGFP[] = []
  @state() private grammemes: GrammemeOption[] = []
  @state() private selectedFeatureId = ''
  @state() private selectedGrammemeId = ''
  @state() private loading = false
  @state() private submitting = false
  @state() private error = ''

  static styles = css`
    ${unsafeCSS(styles)}
  `

  private get featuresForLanguage(): LGFP[] {
    if (!this.languageId) return []
    return this.lgfpList
      .filter((lgfp) => lgfp.language?.id === this.languageId && !this.existingFeatureIds.includes(lgfp.feature?.id ?? ''))
      .sort((a, b) => {
        const pa = parseInt(a.priority?.id ?? '0', 10)
        const pb = parseInt(b.priority?.id ?? '0', 10)
        return pa - pb
      })
  }

  private get grammemesForFeature(): GrammemeOption[] {
    if (!this.selectedFeatureId) return []
    return this.grammemes.filter(
      (g) => g.feature?.id === this.selectedFeatureId && !this.existingGrammemeIds.includes(g.id)
    )
  }

  connectedCallback() {
    super.connectedCallback()
    this.loadData()
  }

  private async loadData() {
    this.loading = true
    this.error = ''
    try {
      const [lgfpRes, gramRes] = await Promise.all([
        apolloClient.query({ query: LANGUAGE_GRAMMATICAL_FEATURE_PRIORITIES_QUERY }),
        apolloClient.query({ query: GRAMMEMES_QUERY }),
      ])
      this.lgfpList = (lgfpRes.data as any).languageGrammaticalFeaturePriorities ?? []
      this.grammemes = (gramRes.data as any).grammemes ?? []
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.loading = false
    }
  }

  private featureLabel(featureId: string, featureName: string): string {
    const out = t(`grammatical_feature_${featureId}.name`)
    return out === `grammatical_feature_${featureId}.name` ? featureName : out
  }

  private grammemeLabel(grammemeId: string, fallbackValue: string): string {
    const out = t(`grammeme_${grammemeId}.value`)
    return out === `grammeme_${grammemeId}.value` ? fallbackValue : out
  }

  private async handleSubmit() {
    if (!this.meaningId || !this.selectedGrammemeId) return
    if (this.existingGrammemeIds.includes(this.selectedGrammemeId)) {
      this.error = t('meaningGrammemes.alreadyAssigned')
      return
    }
    this.submitting = true
    this.error = ''
    try {
      await apolloClient.mutate({
        mutation: CREATE_MEANING_GRAMMEME_MUTATION,
        variables: {
          input: {
            meaningId: this.meaningId,
            grammemeId: this.selectedGrammemeId,
          },
        },
      })
      this.dispatchEvent(new CustomEvent('meaning-grammeme-created', { bubbles: true, composed: true }))
      this.selectedFeatureId = ''
      this.selectedGrammemeId = ''
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.submitting = false
    }
  }

  render() {
    if (!this.meaningId || !this.languageId) return html``
    if (this.loading) return html`<div class="loading">${t('common.loading')}</div>`

    const features = this.featuresForLanguage
    const grammemesForSelected = this.grammemesForFeature

    return html`
      <div class="meaning-grammeme-form">
        <div class="form-row">
          <md-filled-select
            label=${t('meaningGrammemes.grammaticalFeature')}
            .value=${this.selectedFeatureId}
            @change=${(e: Event) => {
              this.selectedFeatureId = (e.target as HTMLSelectElement).value
              this.selectedGrammemeId = ''
            }}
          >
            <md-select-option value=""><div slot="headline">—</div></md-select-option>
            ${features.map((lgfp) => html`
              <md-select-option value=${lgfp.feature?.id ?? ''}>
                <div slot="headline">${this.featureLabel(lgfp.feature?.id ?? '', lgfp.feature?.name ?? '')}</div>
              </md-select-option>
            `)}
          </md-filled-select>
          <md-filled-select
            label=${t('meaningGrammemes.grammeme')}
            .value=${this.selectedGrammemeId}
            ?disabled=${!this.selectedFeatureId}
            @change=${(e: Event) => { this.selectedGrammemeId = (e.target as HTMLSelectElement).value }}
          >
            <md-select-option value=""><div slot="headline">—</div></md-select-option>
            ${grammemesForSelected.map((g) => html`
              <md-select-option value=${g.id}>
                <div slot="headline">${this.grammemeLabel(g.id, g.value)}</div>
              </md-select-option>
            `)}
          </md-filled-select>
        </div>
        ${this.error ? html`<div class="error">${this.error}</div>` : ''}
        <md-filled-button
          class="submit-btn"
          ?disabled=${this.submitting || !this.selectedGrammemeId}
          @click=${this.handleSubmit}
        >
          ${this.submitting ? t('meaningGrammemes.adding') : t('meaningGrammemes.add')}
        </md-filled-button>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'meaning-grammeme-form': MeaningGrammemeForm
  }
}
